import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../services/supabaseClient';
import { Task, TaskSubmission } from '../../types/task';
import TaskDetails from '../../components/tasks/TaskDetails';
import BasePageLayout from '../../components/BasePageLayout';
import CustomDropdown, { DropdownOption } from '../../components/ui/CustomDropdown';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { useDeviceType, DeviceType } from '../../hooks/useDeviceType';
import { useTaskRealtime } from '../../hooks/useTaskRealtime';
import { useLanguage } from '../../context/LanguageContext';
import '../../styles/shared/dashboard.css';
import '../../styles/pages/TasksPage.css';
import '../../styles/pages/TasksHubModern.css';
import '../../styles/pages/CompactTasksHeader.css';
import '../../styles/components/TaskCardWrapper.css';
import './TaskTakerPage.css';

type TaskTakerTab = 'available' | 'submitted' | 'completed' | 'rejected';

const TaskTakerPage: React.FC = () => {
  const [rawAvailableTasks, setRawAvailableTasks] = useState<Task[]>([]);
  const [rawCompletedTasks, setRawCompletedTasks] = useState<(Task & { submission_status: string; submission_id: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'points' | 'category'>('newest');
  const [categories, setCategories] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<TaskTakerTab>('available');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  // Task detail panel state
  const [showTaskDetail, setShowTaskDetail] = useState(false);

  // Responsive state
  const [showMobileView, setShowMobileView] = useState(false);
  const [isCompactView, setIsCompactView] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [tasksPerPage] = useState(10);

  const [stats, setStats] = useState({
    available: 0,
    completed: 0,
    submitted: 0,
    rejected: 0
  });

  // Task detail states
  const [submissions, setSubmissions] = useState<TaskSubmission[]>([]);
  const [userSubmission, setUserSubmission] = useState<TaskSubmission | null>(null);
  const [screenshotDisplayUrl, setScreenshotDisplayUrl] = useState<string | null>(null);
  const [verificationSubmitted, setVerificationSubmitted] = useState(false);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [processingSubmission, setProcessingSubmission] = useState(false);

  const { user } = useAuth();
  const { selectedCountry } = useCountry();
  const { deviceType } = useDeviceType();
  const { t } = useLanguage();

  // Real-time task updates
  const { newTasks, publishedTasks, allNewTasks, clearAllTasks } = useTaskRealtime();

  // Helper function to translate category names
  const translateCategory = useCallback((category: string): string => {
    const categoryMap: Record<string, string> = {
      'Mobile App': t('category.mobileApp'),
      'Website': t('category.website'),
      'Desktop App': t('category.desktopApp'),
      'Game': t('category.game'),
      'SaaS': t('category.saas'),
      'Other': t('category.other')
    };
    return categoryMap[category] || category;
  }, [t]);

  // Dropdown options
  const categoryOptions: DropdownOption[] = useMemo(() => [
    { value: 'all', label: t('tasks.allCategories') },
    ...categories.map(category => ({ value: category, label: translateCategory(category) }))
  ], [categories, t, translateCategory]);

  const statusOptions: DropdownOption[] = useMemo(() => [
    { value: 'all', label: t('tasks.allStatus') },
    { value: 'open', label: t('tasks.open') },
    { value: 'completed', label: t('tasks.completed') }
  ], [t]);

  const sortOptions: DropdownOption[] = useMemo(() => [
    { value: 'newest', label: t('tasks.newestFirst') },
    { value: 'points', label: t('tasks.highestPoints') },
    { value: 'category', label: t('tasks.category') }
  ], [t]);

  // Set responsive states based on device type
  useEffect(() => {
    if (!deviceType) return;

    const isMobile = deviceType === DeviceType.MOBILE;
    const isTablet = deviceType === DeviceType.TABLET;

    setIsCompactView(isMobile || isTablet);
    setShowMobileView(isMobile || isTablet);
  }, [deviceType]);

  // Task Type Buttons Configuration
  const taskTypeButtons = useMemo(() => [
    {
      id: 'available' as TaskTakerTab,
      label: t('tasks.available'),
      hint: t('tasks.tasksYouCanWorkOn'),
      icon: 'bi-list-task',
      count: stats.available,
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
    },
    {
      id: 'submitted' as TaskTakerTab,
      label: t('tasks.submitted'),
      hint: t('tasks.tasksWaitingForReview'),
      icon: 'bi-clock-history',
      count: stats.submitted,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
    },
    {
      id: 'completed' as TaskTakerTab,
      label: t('tasks.completed'),
      hint: t('tasks.successfullyCompletedTasks'),
      icon: 'bi-check-circle-fill',
      count: stats.completed,
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    },
    {
      id: 'rejected' as TaskTakerTab,
      label: t('tasks.rejected'),
      hint: t('tasks.tasksThatNeedRework'),
      icon: 'bi-x-circle-fill',
      count: stats.rejected,
      gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
    }
  ], [stats.available, stats.submitted, stats.completed, stats.rejected, t]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Function to refetch and update task data
  const refetchTaskData = useCallback(async () => {
    if (!user || !selectedCountry) return;

    try {
      // Execute all Supabase calls in parallel for better performance
      const [
        availableTasksResult,
        userSubmissionsResult
      ] = await Promise.all([
        // Fetch available tasks (excluding user's own tasks) with publisher info
        supabase
          .from('tasks')
          .select(`
            *,
            publisher:users!created_by (
              id,
              username,
              avatar_url
            )
          `)
          .neq('created_by', user.id)
          .eq('status', 'open')
          .eq('country_code', selectedCountry),

        // Fetch user's submissions to get completed tasks
        supabase
          .from('task_submissions')
          .select(`
            id,
            task_id,
            status,
            submitted_at,
            tasks (
              id,
              title,
              description,
              points,
              category,
              instructions,
              tags,
              verification_type,
              verification_details,
              status,
              created_by,
              created_at,
              country_code,
              max_participants,
              current_participants
            )
          `)
          .eq('user_id', user.id)
      ]);

      // Check for errors from parallel calls
      if (availableTasksResult.error) throw availableTasksResult.error;
      if (userSubmissionsResult.error) throw userSubmissionsResult.error;

      // Extract data from results
      const availableData = availableTasksResult.data;
      const submissionsData = userSubmissionsResult.data;

      console.log('📋 Raw submissions data:', submissionsData);

      // Process completed tasks with proper typing
      const completedTasksList = (submissionsData || [])
        .map(submission => {
          const taskData = submission.tasks;

          // If task was deleted, create a minimal task object
          if (!taskData) {
            console.log('⚠️ Task not found for submission:', submission.id, 'task_id:', submission.task_id);
            return {
              id: submission.task_id || 'unknown',
              title: 'Deleted Task',
              description: '',
              points: 0,
              category: 'Unknown',
              instructions: '',
              tags: [],
              verification_type: 'screenshot' as const,
              verification_details: {},
              status: 'deleted' as const,
              created_by: '',
              created_at: new Date(),
              country_code: '',
              max_participants: null,
              current_participants: 0,
              submission_status: submission.status,
              submission_id: submission.id
            };
          }

          // Handle the case where taskData might be an array or object
          const task = Array.isArray(taskData) ? taskData[0] : taskData;

          return {
            ...task,
            submission_status: submission.status,
            submission_id: submission.id
          };
        }) as unknown as (Task & { submission_status: string; submission_id: string })[];

      // Filter available tasks to exclude tasks the user has already submitted
      const filteredAvailableData = (availableData as Task[]).filter(task =>
        !completedTasksList.some(completed => completed.id === task.id)
      );

      // Calculate stats
      const submittedCount = completedTasksList.filter(task => task.submission_status === 'pending').length;
      const approvedCount = completedTasksList.filter(task => task.submission_status === 'approved').length;
      const rejectedCount = completedTasksList.filter(task => task.submission_status === 'rejected').length;

      // Update state
      setRawAvailableTasks(filteredAvailableData);
      setRawCompletedTasks(completedTasksList);

      // Update stats
      setStats({
        available: filteredAvailableData.length,
        completed: approvedCount,
        submitted: submittedCount,
        rejected: rejectedCount
      });

      // Extract categories from all task data
      const categorySet = new Set<string>();
      [...(availableData || []), ...completedTasksList].forEach(task => {
        if (task.category) categorySet.add(task.category);
      });
      setCategories(Array.from(categorySet));

      console.log('📊 TaskTaker - Completed tasks breakdown:', {
        total: completedTasksList.length,
        approved: approvedCount,
        pending: submittedCount,
        rejected: rejectedCount,
        statuses: completedTasksList.map(task => ({ id: task.id, status: task.submission_status }))
      });

    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks. Please try again later.');
    }
  }, [user, selectedCountry]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchTasks = async () => {
      if (!user || !selectedCountry) return;

      try {
        setLoading(true);
        setError(null);

        // Use the refetchTaskData function for initial load
        await refetchTaskData();

      } catch (err) {
        console.error('Error fetching tasks:', err);
        setError('Failed to load tasks. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch on user changes
    fetchTasks();
  }, [user?.id, selectedCountry, refetchTaskData]);

  // Update stats when real-time tasks arrive
  useEffect(() => {
    // Only update categories when new tasks arrive, don't modify stats count
    if (allNewTasks.length > 0) {
      // Update categories to include new task categories
      const newCategories = new Set(categories);
      allNewTasks.forEach(task => {
        if (task.category) newCategories.add(task.category);
      });

      // Only update if categories actually changed
      if (newCategories.size !== categories.length) {
        setCategories(Array.from(newCategories));
      }
    }
  }, [allNewTasks.length]); // Only depend on the count, not the array itself

  // Sort function
  const sortTasks = useCallback((tasks: any[]) => {
    const sortedTasks = [...tasks];

    switch (sortBy) {
      case 'newest':
        return sortedTasks.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      case 'points':
        return sortedTasks.sort((a, b) => b.points - a.points);
      case 'category':
        return sortedTasks.sort((a, b) => a.category.localeCompare(b.category));
      default:
        return sortedTasks;
    }
  }, [sortBy]);

  // Filter tasks based on search and filters
  const filteredAvailableTasks = useMemo(() => {
    // Merge raw available tasks with real-time new tasks
    const allAvailableTasks = [...rawAvailableTasks];

    // Add new real-time tasks that aren't already in the list
    allNewTasks.forEach(newTask => {
      if (!allAvailableTasks.find(task => task.id === newTask.id)) {
        allAvailableTasks.unshift(newTask); // Add to beginning for newest first
      }
    });

    const filtered = allAvailableTasks.filter(task => {
      const matchesSearch = !debouncedSearchTerm ||
        task.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        task.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase());

      const matchesCategory = categoryFilter === 'all' || task.category === categoryFilter;
      const matchesStatus = statusFilter === 'all' || task.status === statusFilter;

      return matchesSearch && matchesCategory && matchesStatus;
    });

    // Always include the selected task if it's from available tasks, even if it doesn't match filters
    if (selectedTask && !filtered.find(task => task.id === selectedTask.id)) {
      const selectedTaskInAvailable = allAvailableTasks.find(task => task.id === selectedTask.id);
      if (selectedTaskInAvailable) {
        filtered.push(selectedTaskInAvailable);
      }
    }

    return sortTasks(filtered);
  }, [rawAvailableTasks, allNewTasks, debouncedSearchTerm, categoryFilter, statusFilter, selectedTask, sortTasks]);

  const filteredCompletedTasks = useMemo(() => {
    const filtered = rawCompletedTasks.filter(task => {
      const matchesSearch = !debouncedSearchTerm ||
        task.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        task.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase());

      const matchesCategory = categoryFilter === 'all' || task.category === categoryFilter;

      return matchesSearch && matchesCategory;
    });

    // Always include the selected task if it's from completed tasks, even if it doesn't match filters
    if (selectedTask && !filtered.find(task => task.id === selectedTask.id)) {
      const selectedTaskInCompleted = rawCompletedTasks.find(task => task.id === selectedTask.id);
      if (selectedTaskInCompleted) {
        filtered.push(selectedTaskInCompleted);
      }
    }

    return sortTasks(filtered);
  }, [rawCompletedTasks, debouncedSearchTerm, categoryFilter, selectedTask, sortTasks]);

  // Get current tasks based on active tab
  const getCurrentTasks = useMemo(() => {
    switch (activeTab) {
      case 'available':
        return filteredAvailableTasks;
      case 'submitted':
        return filteredCompletedTasks.filter(task =>
          'submission_status' in task && task.submission_status === 'pending'
        );
      case 'completed':
        return filteredCompletedTasks.filter(task =>
          'submission_status' in task && task.submission_status === 'approved'
        );
      case 'rejected':
        return filteredCompletedTasks.filter(task =>
          'submission_status' in task && task.submission_status === 'rejected'
        );
      default:
        return filteredAvailableTasks;
    }
  }, [activeTab, filteredAvailableTasks, filteredCompletedTasks]);

  // Pagination
  const totalPages = Math.ceil(getCurrentTasks.length / tasksPerPage);
  const currentTasks = getCurrentTasks.slice(
    (currentPage - 1) * tasksPerPage,
    currentPage * tasksPerPage
  );

  const handleTabSwitch = useCallback((tab: TaskTakerTab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedTask(null);
    setShowTaskDetail(false);
    setVerificationSubmitted(false); // Reset verification state when switching tabs
  }, []);

  const handleTaskSelect = useCallback((task: Task) => {
    // Keep the task visible in the list when selected
    setSelectedTask(prevSelected => prevSelected?.id === task.id ? prevSelected : task);
    setShowTaskDetail(true);
    setVerificationSubmitted(false); // Reset verification state when selecting a new task
  }, []);

  // Preserve selected task after data refresh by finding it in the updated lists
  useEffect(() => {
    if (selectedTask) {
      // Find the selected task in the updated data
      const updatedTask = [...rawAvailableTasks, ...rawCompletedTasks].find(
        task => task.id === selectedTask.id
      );

      // Update selectedTask with fresh data if found, otherwise keep the current one
      if (updatedTask && JSON.stringify(updatedTask) !== JSON.stringify(selectedTask)) {
        setSelectedTask(updatedTask);
      }
    }
  }, [rawAvailableTasks, rawCompletedTasks, selectedTask]);

  // Fetch task submissions when a task is selected
  useEffect(() => {
    const fetchSubmissions = async () => {
      if (!selectedTask || !user) return;

      try {
        setSubmissionsLoading(true);

        // Prepare requests array for parallel execution
        const requests = [];

        // Conditionally add creator submissions request
        if (selectedTask.created_by === user.id) {
          requests.push(
            supabase
              .from('task_submissions')
              .select('*')
              .eq('task_id', selectedTask.id)
          );
        } else {
          // Add placeholder promise if user is not the creator
          requests.push(Promise.resolve({ data: null, error: null }));
        }

        // Always add user submission request
        requests.push(
          supabase
            .from('task_submissions')
            .select('*')
            .eq('task_id', selectedTask.id)
            .eq('user_id', user.id)
            .maybeSingle()
        );

        // Execute both requests in parallel
        const [allSubmissionsResult, userSubmissionResult] = await Promise.all(requests);

        // Handle errors
        if (allSubmissionsResult.error) throw allSubmissionsResult.error;
        if (userSubmissionResult.error) throw userSubmissionResult.error;

        // Update state
        if (selectedTask.created_by === user.id && allSubmissionsResult.data) {
          setSubmissions(allSubmissionsResult.data as TaskSubmission[]);
        }
        setUserSubmission(userSubmissionResult.data as TaskSubmission);

      } catch (err) {
        console.error('Error fetching submissions:', err);
      } finally {
        setSubmissionsLoading(false);
      }
    };

    if (selectedTask) {
      fetchSubmissions();
    }
  }, [selectedTask, user, verificationSubmitted]);

  // Handle user submission screenshot display
  useEffect(() => {
    if (user && userSubmission?.proof?.screenshot_url && userSubmission.verification_type === 'screenshot') {
      const getScreenshotUrl = async () => {
        try {
          const screenshotUrl = userSubmission.proof?.screenshot_url;
          if (!screenshotUrl) return;

          const { data, error } = await supabase.storage
            .from('screenshots')
            .createSignedUrl(screenshotUrl, 3600); // 1 hour expiry

          if (error) {
            console.error('Error creating signed URL for user submission:', error);
            return;
          }

          setScreenshotDisplayUrl(data.signedUrl);
        } catch (error) {
          console.error('Error in getScreenshotUrl:', error);
        }
      };

      getScreenshotUrl();
    } else {
      setScreenshotDisplayUrl(null);
    }
  }, [user, userSubmission]);

  // Update stats based on actual filtered task counts (not incrementally)
  useEffect(() => {
    const submittedCount = filteredCompletedTasks.filter(task =>
      'submission_status' in task && task.submission_status === 'pending'
    ).length;
    const approvedCount = filteredCompletedTasks.filter(task =>
      'submission_status' in task && task.submission_status === 'approved'
    ).length;
    const rejectedCount = filteredCompletedTasks.filter(task =>
      'submission_status' in task && task.submission_status === 'rejected'
    ).length;

    setStats({
      available: filteredAvailableTasks.length, // Use actual count including real-time tasks
      completed: approvedCount,
      submitted: submittedCount,
      rejected: rejectedCount
    });
  }, [filteredAvailableTasks.length, filteredCompletedTasks.length]);

  if (loading) {
    return (
      <BasePageLayout background="white" padding="none" maxWidth="none">
        <div className="loading-container">
          <div className="loading-ring"></div>
          <p>{t('tasks.loadingTasks')}</p>
        </div>
      </BasePageLayout>
    );
  }

  if (error) {
    return (
      <BasePageLayout background="white" padding="none" maxWidth="none">
        <div className="error-container">
          <h3>{t('common.error')}</h3>
          <p>{error}</p>
        </div>
      </BasePageLayout>
    );
  }

  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="tasks-hub-container">
        {/* Compact Header */}
        <div className="compact-tasks-header">
          <div className="compact-header-content">
            {/* Title Section */}
            <div className="compact-title-section">
              <div>
                <h1 className="compact-page-title">{t('tasks.taskTakerDashboard')}</h1>
                <div className="compact-breadcrumb">
                  <span>{t('navigation.tasks')}</span>
                  <i className="bi bi-chevron-right compact-breadcrumb-separator"></i>
                  <span>{t('tasks.taker')}</span>
                </div>
              </div>
              <div className="compact-stats-mini">
                <div className="stat-item-mini">
                  <span className="stat-number-mini">{stats.available + stats.submitted + stats.completed + stats.rejected}</span>
                  <span className="stat-label-mini">{t('tasks.totalTasks')}</span>
                </div>
                <div className="stat-item-mini earnings">
                  <span className="stat-number-mini">
                    {rawCompletedTasks
                      .filter(task => task.submission_status === 'approved')
                      .reduce((total, task) => total + task.points, 0)}
                  </span>
                  <span className="stat-label-mini">{t('tasks.pointsEarned')}</span>
                </div>
              </div>
            </div>

            {/* Controls Section */}
            <div className="compact-controls-section">
              {/* Task Type Buttons */}
              <div className="compact-type-buttons">
                {taskTypeButtons.map(button => (
                  <button
                    key={button.id}
                    className={`compact-type-button compact-tooltip ${activeTab === button.id ? 'active' : ''} ${button.count === 0 ? 'disabled' : ''}`}
                    onClick={() => button.count > 0 && handleTabSwitch(button.id)}
                    disabled={button.count === 0}
                    data-tooltip={button.hint}
                    style={{
                      background: activeTab === button.id ? button.gradient : undefined
                    }}
                  >
                    <i className={`bi ${button.icon} compact-type-button-icon`}></i>
                    <span className="compact-type-button-label">{button.label}</span>
                    <span className="compact-type-button-count">{button.count}</span>
                  </button>
                ))}
              </div>

              {/* Search and Filters */}
              <div className="compact-search-filters">
                <div className="compact-search-bar">
                  <i className="bi bi-search compact-search-icon"></i>
                  <input
                    type="text"
                    className="compact-search-input"
                    placeholder={t('tasks.searchTasks')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="compact-filter-dropdown">
                  <CustomDropdown
                    options={categoryOptions}
                    value={categoryFilter}
                    onChange={setCategoryFilter}
                    placeholder={t('tasks.category')}
                    size="small"
                  />
                </div>

                <div className="compact-filter-dropdown">
                  <CustomDropdown
                    options={statusOptions}
                    value={statusFilter}
                    onChange={setStatusFilter}
                    placeholder={t('common.filter')}
                    size="small"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`tasks-hub-main-container ${showTaskDetail ? 'details-active' : ''}`}>
          {/* Task List */}
          <div className="tasks-hub-task-list-column">
            <div className="tasks-hub-task-list-header">
              <div className="tasks-hub-sort-controls">
                <button
                  onClick={refetchTaskData}
                  className="tasks-hub-refresh-button"
                  title={t('tasks.refreshTaskList')}
                  disabled={loading}
                >
                  <i className={`bi bi-arrow-clockwise ${loading ? 'spinning' : ''}`}></i>
                </button>

                <div className="compact-filter-dropdown">
                  <CustomDropdown
                    options={sortOptions}
                    value={sortBy}
                    onChange={(value) => setSortBy(value as 'newest' | 'points' | 'category')}
                    placeholder={t('tasks.sortBy')}
                    size="small"
                  />
                </div>
              </div>
            </div>

            <div className="tasks-hub-task-list-content">
              {/* Task Cards */}
              <div className="tasks-hub-task-cards">
                {currentTasks.length === 0 ? (
                  <div className="no-tasks-message">
                    <i className="bi bi-inbox"></i>
                    <h3>{t('tasks.noTasksFound')}</h3>
                    <p>
                      {activeTab === 'available'
                        ? t('tasks.noTasksInYourArea')
                        : t('tasks.noTasksMatchFilter')
                      }
                    </p>
                    {activeTab === 'available' && (
                      <p className="text-secondary">{t('tasks.checkBackLater')}</p>
                    )}
                  </div>
                ) : (
                  currentTasks.map(task => {
                    const isCompleted = activeTab !== 'available';
                    const submissionStatus = isCompleted && 'submission_status' in task ?
                      (task as Task & { submission_status: string }).submission_status : null;

                    return (
                      <div
                        key={task.id}
                        className={`task-card-wrapper ${selectedTask?.id === task.id ? 'selected' : ''}`}
                        onClick={() => handleTaskSelect(task)}
                      >
                        <div className="task-card task-card-modern">
                          <div className="task-header-row">
                            <h3 className="task-title">{task.title}</h3>
                            <div className="task-status-badges">
                              {submissionStatus && (
                                <span className={`task-status-badge ${submissionStatus}`}>
                                  <i className={`bi ${submissionStatus === 'approved' ? 'bi-check-circle-fill' :
                                    submissionStatus === 'pending' ? 'bi-clock-history' :
                                      submissionStatus === 'rejected' ? 'bi-x-circle-fill' : 'bi-circle'
                                    }`}></i>
                                  {submissionStatus}
                                </span>
                              )}
                              <span className="task-points">
                                <i className="bi bi-star-fill"></i>
                                {task.points}
                              </span>
                            </div>
                          </div>

                          <p className="task-description">{task.description}</p>

                          <div className="task-meta">
                            <div className="task-meta-left">
                              <span className="task-category">
                                <i className="bi bi-tag"></i>
                                {translateCategory(task.category)}
                              </span>
                              <span className="task-verification-info">
                                <i className={`bi task-verification-icon ${task.verification_type === 'screenshot' ? 'bi-camera' :
                                  task.verification_type === 'text_response' ? 'bi-chat-text' :
                                    task.verification_type === 'email_username' ? 'bi-envelope' : 'bi-check'
                                  }`}></i>
                                {task.verification_type === 'screenshot' ? t('tasks.screenshot') :
                                  task.verification_type === 'text_response' ? t('tasks.textResponse') :
                                    task.verification_type === 'email_username' ? t('tasks.emailUsername') : t('tasks.verification')}
                              </span>
                              <span className="task-participants">
                                <i className="bi bi-people"></i>
                                {task.current_participants || 0}/{task.max_participants || 'N/A'}
                              </span>
                            </div>
                            <div className="task-meta-right">
                              <span className="task-date">
                                {new Date(task.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination-container">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                )}

              </div>
            </div>

          </div>

          {/* Overlay for when detail panel is open */}
          {showTaskDetail && (
            <div
              className="task-detail-overlay"
              onClick={() => setShowTaskDetail(false)}
            />
          )}

          {/* Task Detail Panel - Slide out from right */}
          <div className={`task-detail-panel ${showTaskDetail ? 'panel-open' : ''}`}>
            {selectedTask ? (
              <div className="task-detail-content">
                <div className="task-detail-header">
                  <div className="task-detail-header-main">
                    <div className="task-detail-title-area">
                      <h1 className="task-detail-title">{selectedTask.title}</h1>
                      <div className="task-detail-metadata">
                        <span className="task-detail-category-badge">
                          <i className="bi bi-tag-fill"></i>
                          {translateCategory(selectedTask.category)}
                        </span>
                        <span className="task-detail-points-badge">
                          <i className="bi bi-star-fill"></i>
                          {selectedTask.points} pts
                        </span>
                        <span className="task-detail-participants-badge">
                          <i className="bi bi-people-fill"></i>
                          {selectedTask.current_participants || 0}
                          {selectedTask.max_participants ? `/${selectedTask.max_participants}` : ''}
                        </span>
                        <span className="task-detail-date-badge">
                          <i className="bi bi-calendar3"></i>
                          {new Date(selectedTask.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                    </div>

                    <div className="task-detail-header-actions">
                      <button
                        className="task-detail-action-btn new-tab"
                        onClick={() => {
                          const taskUrl = `/tasks/${selectedTask.id}`;
                          window.open(taskUrl, '_blank');
                        }}
                        title="Open in new tab"
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                      </button>

                      <button
                        className="close-detail-btn"
                        onClick={() => setShowTaskDetail(false)}
                        title="Close"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <TaskDetails
                  selectedTask={selectedTask}
                  user={user}
                  userSubmission={userSubmission}
                  submissions={submissions}
                  submissionsLoading={submissionsLoading}
                  screenshotDisplayUrl={screenshotDisplayUrl}
                  submissionScreenshotUrls={{}}
                  verificationSubmitted={verificationSubmitted}
                  onVerificationSubmitted={async () => {
                    if (processingSubmission) return; // Prevent multiple submissions

                    setProcessingSubmission(true);
                    setVerificationSubmitted(true);

                    try {
                      // Refresh data to update task lists and counts
                      await refetchTaskData();

                      // Switch to submitted tab to show the newly submitted task
                      setActiveTab('submitted');

                      // Close the detail panel and clear selection
                      setShowTaskDetail(false);
                      setSelectedTask(null);
                    } catch (error) {
                      console.error('Error refreshing data after submission:', error);
                    } finally {
                      setProcessingSubmission(false);
                    }
                  }}
                  onTaskClose={() => {
                    setShowTaskDetail(false);
                    setSelectedTask(null);
                  }}
                  onBackToTaskList={() => {
                    setShowTaskDetail(false);
                    setSelectedTask(null);
                  }}
                  showMobileView={showMobileView}
                  sidebarCollapsed={false}
                  onUpdateSubmission={async () => {
                    if (processingSubmission) return; // Prevent multiple submissions

                    setProcessingSubmission(true);

                    try {
                      // Refresh data to get updated submission status
                      await refetchTaskData();

                      // Stay on the current tab but update the selected task data
                      // to show the updated submission status
                    } catch (error) {
                      console.error('Error refreshing data after resubmission:', error);
                    } finally {
                      setProcessingSubmission(false);
                    }
                  }}
                />
              </div>
            ) : null}
          </div>

        </div>
      </div>
    </BasePageLayout>
  );
};

export default TaskTakerPage;
